import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type AccountOpeningCollection, type AccountOpeningStep, type AccountOpeningStepId } from '../../../models/AccountOpeningDescriptor'
import { collectionEntrySteps, flowCollections } from '../domain/collections'
import { type PreparedSchemas } from './useCollectionSchemas'

interface CollectionPreparation {
  isHydrating: boolean
  isFilling: boolean
  failed: boolean
}

const IDLE: CollectionPreparation = { isHydrating: false, isFilling: false, failed: false }

const FAILED: CollectionPreparation = { isHydrating: false, isFilling: false, failed: true }

interface CollectionPreparations {
  of: (collection: AccountOpeningCollection) => CollectionPreparation
  reset: () => void
}

export const useCollectionPreparations = ({
  steps,
  reachedStep,
  signatures,
  hydrate,
  fill,
}: {
  steps: AccountOpeningStep[]
  reachedStep: (id: AccountOpeningStepId) => boolean
  signatures: Record<string, string>
  hydrate: (collection: AccountOpeningCollection) => Promise<PreparedSchemas>
  fill: (collection: AccountOpeningCollection, prepared: PreparedSchemas) => Promise<void>
}): CollectionPreparations => {
  const [phases, setPhases] = useState<Record<string, CollectionPreparation>>({})
  const attempted = useRef<Set<string>>(new Set())

  const collections = useMemo(() => flowCollections(steps), [steps])
  const entrySteps = useMemo(() => collectionEntrySteps(steps), [steps])

  const readyKey = collections.map((collection) => (reachedStep(entrySteps.get(collection) ?? '') ? '1' : '0')).join('')

  const setPhase = useCallback((collection: AccountOpeningCollection, phase: CollectionPreparation) => {
    setPhases((previous) => ({ ...previous, [collection]: phase }))
  }, [])

  useEffect(() => {
    collections.forEach((collection) => {
      const signature = signatures[collection] ?? ''
      const entryStep = entrySteps.get(collection)
      if (!signature || !entryStep || !reachedStep(entryStep)) {
        return
      }
      const attempt = `${collection}:${signature}`
      if (attempted.current.has(attempt)) {
        return
      }
      attempted.current.add(attempt)
      setPhase(collection, { isHydrating: true, isFilling: false, failed: false })
      hydrate(collection)
        .then((prepared) => {
          setPhase(collection, { isHydrating: false, isFilling: true, failed: false })
          return fill(collection, prepared)
        })
        .then(() => setPhase(collection, IDLE))
        .catch(() => {
          attempted.current.delete(attempt)
          setPhase(collection, FAILED)
        })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `readyKey` stands in for `reachedStep`, a fresh closure on every render
  }, [collections, entrySteps, signatures, readyKey, hydrate, fill, setPhase])

  const of = useCallback((collection: AccountOpeningCollection) => phases[collection] ?? IDLE, [phases])
  const reset = useCallback(() => {
    attempted.current.clear()
    setPhases({})
  }, [])

  return { of, reset }
}
